import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  creditNoteQuery,
  creditReasonsQuery,
  creditState,
  discardCreditNote,
  issueCreditNote,
  refundCredit,
  saveCreditNote,
  type CreditNote,
  type CreditNoteInput,
  type CreditReason,
} from '../api/credit-notes.ts';
import { renderCreditNoteDocument } from '../api/documents.ts';
import {
  invoiceQuery,
  PAYMENT_METHODS,
  type Invoice,
  type PaymentMethod,
} from '../api/invoices.ts';
import { meQuery } from '../api/me.ts';
import { dayLabel, taxCodesQuery } from '../api/quotes.ts';
import { AddButton, RemoveButton } from '../components/action-buttons.tsx';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { dollarsToMinorUnits, formatMinorUnits, minorUnitsToDollars } from '../money.ts';
import { DocumentsPanel } from './documents-panel.tsx';

// One credit (CB-REQ FIN-02 at v1.993). A draft is filled in here — lines of its invoice by quantity
// or by an amount, set amounts, a reason — and issued as an adjustment note, numbered AN- and final.
// Credit it holds beyond what its invoice owed is refunded here, or applied from the other invoice.

type TaxCode = { id: string; code: string; name: string };

export function CreditNoteScreen() {
  const { creditNoteId } = useParams({ from: '/authenticated/credit-notes/$creditNoteId' });
  const note = useQuery(creditNoteQuery(creditNoteId));
  const invoice = useQuery({
    ...invoiceQuery(note.data?.invoice.id ?? ''),
    enabled: !!note.data,
  });
  const reasons = useQuery(creditReasonsQuery);
  const taxCodes = useQuery(taxCodesQuery);
  const me = useQuery(meQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mayManage = me.data?.capabilities.includes('invoices.manage') ?? false;
  const today = me.data ? Temporal.Now.plainDateISO(me.data.tenant.defaultTz).toString() : '';

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };
  const save = useMutation({
    mutationFn: (input: CreditNoteInput) => saveCreditNote(creditNoteId, input),
    onSuccess: refresh,
  });
  const issue = useMutation({
    mutationFn: async (input: CreditNoteInput) => {
      await saveCreditNote(creditNoteId, input);
      return issueCreditNote(creditNoteId);
    },
    onSuccess: refresh,
  });
  const discard = useMutation({
    mutationFn: () => discardCreditNote(creditNoteId),
    onSuccess: async () => {
      await refresh();
      await navigate({ to: '/invoices/$invoiceId', params: { invoiceId: note.data!.invoice.id } });
    },
  });
  const refund = useMutation({
    mutationFn: (input: Parameters<typeof refundCredit>[1]) => refundCredit(creditNoteId, input),
    onSuccess: refresh,
  });

  if (note.isPending) return <p className="text-muted">Loading…</p>;
  if (note.isError) {
    return (
      <p role="alert" className="alert">
        {note.error.message}
      </p>
    );
  }
  const c = note.data;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/credit-notes" className="link self-start text-sm">
        ← Credits
      </Link>
      <PageHeader
        kicker={`Credit · ${creditState(c)}`}
        title={c.reference ?? 'Draft credit'}
        meta={`${c.clientName} · ${c.invoice.reference}`}
        actions={
          <Link
            to="/invoices/$invoiceId"
            params={{ invoiceId: c.invoice.id }}
            className="link text-sm"
          >
            {c.invoice.reference}
          </Link>
        }
      />

      {c.status === 'draft' && c.byDatabase ? (
        <p className="notice">
          AVenue drafted this when {c.invoice.reference}’s job was{' '}
          {c.reason?.kind === 'cancelled' ? 'cancelled' : 'revised'}. Check it, then issue it.
        </p>
      ) : null}

      {c.status === 'draft' && mayManage && invoice.data && reasons.data && taxCodes.data ? (
        <CreditEditor
          note={c}
          invoice={invoice.data}
          reasons={reasons.data}
          taxCodes={taxCodes.data}
          pending={save.isPending || issue.isPending}
          error={save.error ?? issue.error}
          onSave={(input) => save.mutate(input)}
          onIssue={(input) => issue.mutate(input)}
          onDiscard={() => discard.mutate()}
        />
      ) : null}

      <AdjustmentNote note={c} />

      {c.status === 'issued' ? <CreditMoney note={c} /> : null}
      {c.status === 'issued' && mayManage && BigInt(c.held.amountMinor) > 0n ? (
        <RefundForm
          held={c.held}
          today={today}
          pending={refund.isPending}
          error={refund.error}
          onSubmit={(input) => refund.mutate(input)}
        />
      ) : null}

      <DocumentsPanel
        owner="credit_note"
        id={c.id}
        actions={[
          {
            label: c.status === 'issued' ? 'Adjustment note PDF' : 'Draft PDF',
            prepare: () => renderCreditNoteDocument(c.id),
          },
        ]}
      />
    </div>
  );
}

/** A line of the invoice as the editor holds it: nothing, a quantity, or dollars. */
interface LineChoice {
  readonly quantity: string;
  readonly dollars: string;
}

export function CreditEditor({
  note,
  invoice,
  reasons,
  taxCodes,
  pending,
  error,
  onSave,
  onIssue,
  onDiscard,
}: {
  note: CreditNote;
  invoice: Invoice;
  reasons: CreditReason[];
  taxCodes: TaxCode[];
  pending: boolean;
  error: Error | null;
  onSave: (input: CreditNoteInput) => void;
  onIssue: (input: CreditNoteInput) => void;
  onDiscard: () => void;
}) {
  const [choices, setChoices] = useState<Record<string, LineChoice>>(() =>
    Object.fromEntries(
      note.lines
        .filter((l) => l.invoiceLineId)
        .map((l) => [
          l.invoiceLineId!,
          l.quantity === null
            ? { quantity: '', dollars: minorUnitsToDollars(l.total.amountMinor) }
            : { quantity: String(l.quantity), dollars: '' },
        ]),
    ),
  );
  const [amounts, setAmounts] = useState(
    note.lines
      .filter((l) => !l.invoiceLineId)
      .map((l) => ({
        description: l.description,
        dollars: minorUnitsToDollars(l.total.amountMinor),
        taxCodeId: taxCodes.find((t) => t.code === l.taxCode)?.id ?? taxCodes[0]?.id ?? '',
      })),
  );
  const [reasonId, setReasonId] = useState(note.reason?.id ?? '');
  const [text, setText] = useState(note.note ?? '');
  const [problem, setProblem] = useState<string | undefined>();
  const live = reasons.filter((r) => !r.retired || r.id === note.reason?.id);
  const kind = reasons.find((r) => r.id === reasonId)?.kind;

  function input(): CreditNoteInput | null {
    const lines: Array<CreditNoteInput['lines'][number]> = [];
    for (const l of invoice.lines) {
      const choice = choices[l.id];
      if (!choice) continue;
      if (choice.dollars.trim()) {
        const amountMinor = dollarsToMinorUnits(choice.dollars);
        if (!amountMinor) return fail('An amount in dollars and cents, like 120.50.');
        lines.push({ invoiceLineId: l.id, amountMinor });
      } else if (choice.quantity.trim()) {
        const quantity = Number(choice.quantity);
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > l.quantity) {
          return fail(`A quantity from 1 to ${l.quantity}.`);
        }
        lines.push({ invoiceLineId: l.id, quantity });
      }
    }
    const set: Array<CreditNoteInput['amounts'][number]> = [];
    for (const a of amounts) {
      const amountMinor = dollarsToMinorUnits(a.dollars);
      if (!a.description.trim() || !amountMinor)
        return fail('Each amount needs a name and dollars.');
      set.push({ description: a.description.trim(), amountMinor, taxCodeId: a.taxCodeId });
    }
    if (kind === 'other' && !text.trim()) return fail('Say why: Other needs a note.');
    setProblem(undefined);
    return { reasonId: reasonId || null, note: text.trim() || null, lines, amounts: set };
  }
  function fail(message: string): null {
    setProblem(message);
    return null;
  }
  const submit = (then: (i: CreditNoteInput) => void) => (event?: FormEvent) => {
    event?.preventDefault();
    const value = input();
    if (value) then(value);
  };
  const choose = (lineId: string, change: Partial<LineChoice>) =>
    setChoices((all) => ({
      ...all,
      [lineId]: { ...(all[lineId] ?? { quantity: '', dollars: '' }), ...change },
    }));

  return (
    <form
      onSubmit={submit(onSave)}
      aria-labelledby="credit-what"
      className="flex flex-col gap-4 border-2 border-divider p-4"
    >
      <h2 id="credit-what" className="section-title">
        What it credits
      </h2>
      <div className="overflow-x-auto">
        <table className="data-table">
          <caption className="sr-only">The invoice’s lines: credit a quantity or dollars</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col" className="text-right">
                Charged
              </th>
              <th scope="col">Qty</th>
              <th scope="col">Or $</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.id}>
                <th scope="row" className="font-normal">
                  {l.description}
                  <span className="block text-xs text-ink-soft">of {l.quantity}</span>
                </th>
                <td className="text-right tabular-nums">
                  {formatMinorUnits(l.total.amountMinor, l.total.currency)}
                </td>
                <td>
                  <input
                    className="input w-20"
                    inputMode="numeric"
                    aria-label={`Quantity of ${l.description}`}
                    value={choices[l.id]?.quantity ?? ''}
                    onChange={(e) => choose(l.id, { quantity: e.currentTarget.value, dollars: '' })}
                  />
                </td>
                <td>
                  <input
                    className="input w-28"
                    inputMode="decimal"
                    aria-label={`Dollars off ${l.description}`}
                    value={choices[l.id]?.dollars ?? ''}
                    onChange={(e) => choose(l.id, { dollars: e.currentTarget.value, quantity: '' })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {amounts.map((a, index) => (
        <div
          key={index}
          className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[2fr_1fr_1fr_auto]"
        >
          <Field
            label="Amount for"
            value={a.description}
            onChange={(e) => {
              const description = e.currentTarget.value;
              setAmounts((all) => all.map((x, i) => (i === index ? { ...x, description } : x)));
            }}
          />
          <Field
            label="$ ex GST"
            inputMode="decimal"
            value={a.dollars}
            onChange={(e) => {
              const dollars = e.currentTarget.value;
              setAmounts((all) => all.map((x, i) => (i === index ? { ...x, dollars } : x)));
            }}
          />
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-ink-soft">Tax</span>
            <select
              className="input"
              value={a.taxCodeId}
              onChange={(e) => {
                const taxCodeId = e.currentTarget.value;
                setAmounts((all) => all.map((x, i) => (i === index ? { ...x, taxCodeId } : x)));
              }}
            >
              {taxCodes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <RemoveButton
            what={`the amount ${a.description || index + 1}`}
            type="button"
            onClick={() => setAmounts((all) => all.filter((_, i) => i !== index))}
          />
        </div>
      ))}
      <div>
        <AddButton
          noun="Amount"
          type="button"
          variant="quiet"
          onClick={() =>
            setAmounts((all) => [
              ...all,
              { description: '', dollars: '', taxCodeId: taxCodes[0]?.id ?? '' },
            ])
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Reason</span>
          <select
            className="input"
            value={reasonId}
            onChange={(e) => setReasonId(e.currentTarget.value)}
          >
            <option value="">Pick one</option>
            {live.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <Field
          label={kind === 'other' ? 'Note' : 'Note (optional)'}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
      </div>

      {problem || error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? error?.message}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="quiet" disabled={pending}>
          Save
        </Button>
        <Button type="button" disabled={pending} onClick={() => submit(onIssue)()}>
          {pending ? 'Issuing…' : 'Issue'}
        </Button>
        <RemoveButton what="this draft credit" type="button" onClick={onDiscard} />
      </div>
    </form>
  );
}

/** The document: what an adjustment note states, as the PDF prints it. */
export function AdjustmentNote({ note: c }: { note: CreditNote }) {
  return (
    <article
      aria-labelledby="credit-title"
      className="flex flex-col gap-5 border-2 border-divider p-4 sm:p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 id="credit-title" className="text-xl font-extrabold">
            {c.status === 'issued' ? 'Adjustment note' : 'Draft — not yet an adjustment note'}
          </h2>
          <p className="mt-1 font-semibold">{c.issuer.legalName}</p>
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-sm">
          <dt className="text-ink-soft">Number</dt>
          <dd className="font-semibold">{c.reference ?? 'On issue'}</dd>
          <dt className="text-ink-soft">Issued</dt>
          <dd>{c.issueDate ? dayLabel(c.issueDate) : '—'}</dd>
          <dt className="text-ink-soft">Adjusts</dt>
          <dd>
            {c.invoice.reference}, {dayLabel(c.invoiceIssueDate)}
          </dd>
          <dt className="text-ink-soft">Reason</dt>
          <dd>
            {c.reason?.label ?? '—'}
            {c.note ? `: ${c.note}` : ''}
          </dd>
        </dl>
      </header>
      {c.lines.length === 0 ? (
        <p className="text-ink-soft">Nothing credited yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <caption className="sr-only">What this credit takes off, ex GST</caption>
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col" className="text-right">
                  Qty
                </th>
                <th scope="col">Tax</th>
                <th scope="col" className="text-right">
                  Credit ex GST
                </th>
              </tr>
            </thead>
            <tbody>
              {c.lines.map((l) => (
                <tr key={l.id}>
                  <th scope="row" className="font-normal">
                    {l.description}
                  </th>
                  <td className="text-right tabular-nums">{l.quantity ?? ''}</td>
                  <td>{l.taxCode}</td>
                  <td className="text-right tabular-nums">
                    {formatMinorUnits(l.total.amountMinor, l.total.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <dl className="ml-auto grid grid-cols-[auto_auto] gap-x-6 gap-y-1 text-right tabular-nums">
        <dt className="text-ink-soft">Subtotal</dt>
        <dd>{formatMinorUnits(c.totals.subtotal.amountMinor, c.totals.subtotal.currency)}</dd>
        <dt className="text-ink-soft">GST</dt>
        <dd>{formatMinorUnits(c.totals.tax.amountMinor, c.totals.tax.currency)}</dd>
        <dt className="font-extrabold">Total</dt>
        <dd className="font-extrabold">
          {formatMinorUnits(c.totals.total.amountMinor, c.totals.total.currency)}
        </dd>
      </dl>
      {allocationBack(c) > 0n ? (
        <p className="text-sm text-ink-soft">
          {formatMinorUnits(allocationBack(c).toString(), c.totals.total.currency)} of the
          complimentary allocation given back.
        </p>
      ) : null}
    </article>
  );
}

/** VEN-11 at v1.993: what of the venue's allocation this credit gives back. */
function allocationBack(c: CreditNote): bigint {
  return c.lines.reduce((n, l) => n + BigInt(l.allocation.amountMinor), 0n);
}

/** Credit held, and where what was held went. */
function CreditMoney({ note: c }: { note: CreditNote }) {
  if (c.refunds.length === 0 && c.applications.length === 0 && BigInt(c.held.amountMinor) === 0n) {
    return null;
  }
  return (
    <section className="flex flex-col gap-2" aria-labelledby="credit-held">
      <h2 id="credit-held" className="section-title">
        Credit
      </h2>
      {BigInt(c.held.amountMinor) > 0n ? (
        <p>
          <span className="font-extrabold">
            {formatMinorUnits(c.held.amountMinor, c.held.currency)}
          </span>{' '}
          held. Refund it, or apply it from another of {c.clientName}’s invoices.
        </p>
      ) : null}
      {c.refunds.map((r, index) => (
        <p key={`r${index}`}>
          {formatMinorUnits(r.amount.amountMinor, r.amount.currency)} refunded by{' '}
          {(PAYMENT_METHODS.find(([m]) => m === r.method)?.[1] ?? r.method).toLowerCase()}
          {r.reference ? ` · ${r.reference}` : ''}.
        </p>
      ))}
      {c.applications.map((a, index) => (
        <p key={`a${index}`}>
          {formatMinorUnits(a.amount.amountMinor, a.amount.currency)} applied to{' '}
          <Link to="/invoices/$invoiceId" params={{ invoiceId: a.invoice.id }} className="link">
            {a.invoice.reference}
          </Link>
          .
        </p>
      ))}
    </section>
  );
}

export function RefundForm({
  held,
  today,
  pending,
  error,
  onSubmit,
}: {
  held: CreditNote['held'];
  today: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (input: Parameters<typeof refundCredit>[1]) => void;
}) {
  const [amount, setAmount] = useState(minorUnitsToDollars(held.amountMinor));
  const [refundedOn, setRefundedOn] = useState(today);
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [problem, setProblem] = useState<string | undefined>();

  function submit(event: FormEvent) {
    event.preventDefault();
    const amountMinor = dollarsToMinorUnits(amount);
    if (!amountMinor) return setProblem('An amount in dollars and cents, like 120.50.');
    if (method === 'other' && !reference.trim()) {
      return setProblem('Say what it was: Other needs a reference.');
    }
    setProblem(undefined);
    onSubmit({ amountMinor, refundedOn, method, reference: reference.trim() || null });
  }

  return (
    <form
      onSubmit={submit}
      aria-labelledby="refund"
      className="flex flex-col gap-4 border-2 border-divider p-4"
    >
      <h2 id="refund" className="section-title">
        Refund
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Amount ($)"
          inputMode="decimal"
          required
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
        />
        <Field
          label="Paid on"
          type="date"
          required
          max={today || undefined}
          value={refundedOn}
          onChange={(e) => setRefundedOn(e.currentTarget.value)}
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Method</span>
          <select
            className="input"
            value={method}
            onChange={(e) => setMethod(e.currentTarget.value as PaymentMethod)}
          >
            {PAYMENT_METHODS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <Field
          label={method === 'other' ? 'Reference' : 'Reference (optional)'}
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
        />
      </div>
      {problem || error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? error?.message}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={pending}>
          {pending ? 'Recording…' : 'Record refund'}
        </Button>
      </div>
    </form>
  );
}

/** For the list: when it issued, or that it is a draft. */
export function issuedLabel(c: { issueDate?: string | null }): string {
  return c.issueDate ? dayLabel(c.issueDate) : '—';
}
